import React, { useRef } from "react";
import PropTypes from "prop-types";
import createMarkup from "create-markup";
import { graphql } from "gatsby";
import { useTranslation } from "react-i18next";
import locales from "../../i18n/config";

// hoc
import withI18next from "../../i18n/withI18next";

// hooks
import useSmallScreen from "../../hooks/useSmallScreen";

// components
import Layout from "../../components/layout";
import StickyWrapper from "../../components/StickyWrapper/StickyWrapper";
import ArticleContentWrapper from "../../components/ArticleContentWrapper/ArticleContentWrapper";
import ArticleContent from "../../components/ArticleContent/ArticleContent";
import LocationIcon from "../../icons/LocationIcon";
import TimeIcon from "../../icons/TimeIcon";
import CalendarIcon from "../../icons/CalendarIcon";
import BackgroundWithLinesHalf from "../../components/Background/BackgroundWithLinesHalf";
import SectionWhiteFirst from "../../components/PageSections/SectionWhiteFirst";
import SectionWhiteHalfPage from "../../components/PageSections/SectionWhiteHalfPage";

// styles
import "./event-brite-page.scss";

const EventBritePage = ({ data, location, pageContext }) => {
  const { smallScreen } = useSmallScreen()
  const layoutRef = useRef(null);
  const { t } = useTranslation();

  const { eventBriteEvent } = data;
  const {
    name,
    end,
    start,
    url,
    description,
    logo,
    venue,
    locale,
  } = eventBriteEvent;

  // Remove first div that contains summary
  const longDescription = description.replace(/<div>(.*?)<\/div>/i, "")

  const updateLogoColor = (color) => {
    layoutRef.current.handleLogoColor(color);
  };

  const getLanguageSelectorLocalizationForEvent = () => {
    if (locale !== locales["en"].eventbriteLanguage) {
      return [pageContext.locale];
    }
    return Object.values(locales).map((item) => item.name);
  };

  return (
    <>
      <Layout
        title={name.text}
        description={longDescription}
        ogImage={logo.url}
        location={location}
        ref={layoutRef}
        language
        languageSelectorInformation={{
          fallback: "/nyheter",
          localesList: getLanguageSelectorLocalizationForEvent(),
        }}
        halfPage
      >
        <div className="event-page">
          <div className="event-page__container">
            <div className="--left">
              <StickyWrapper>
                <div className="event-page__sticky-content">
                  <div className="event-page__group-item">
                    <CalendarIcon />
                    <span className="date">{start.utc}</span>
                  </div>
                  <div className="event-page__group-item">
                    <TimeIcon />
                    <span>
                      {t(`eventbritePage.clock`, "Kl")} {start.local} -{" "}
                      {end.local}
                    </span>
                  </div>
                  <div className="event-page__group-item">
                    <LocationIcon />
                    {venue && venue.address ? (
                      <span>
                        {venue.address.address_1}, {venue.address.city}
                      </span>
                    ) : (
                      <span>{t(`eventbritePage.location`, "Online")}</span>
                    )}
                  </div>
                  <a
                    href={url + "#tickets"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="event-page__sign-up-link"
                  >
                    <span>
                      {t(`eventbritePage.buttonLabel`, "ANMÄL DIG HÄR")}
                    </span>
                  </a>
                </div>
              </StickyWrapper>
            </div>
            <div className="--right">
              <BackgroundWithLinesHalf theme={"white"}>
                {smallScreen && (
                  <SectionWhiteFirst logoColor={updateLogoColor} />
                )}
                {!smallScreen && (
                  <SectionWhiteHalfPage logoColor={updateLogoColor} />
                )}
                <ArticleContentWrapper>
                  <ArticleContent>
                    <h1 className="event-page__event-title">{name.text}</h1>
                    <div
                      className="event-page__description-text"
                      dangerouslySetInnerHTML={createMarkup(longDescription)}
                    />
                  </ArticleContent>
                </ArticleContentWrapper>
              </BackgroundWithLinesHalf>
            </div>
          </div>
        </div>
      </Layout>
    </>
  );
};

export default withI18next()(EventBritePage);

EventBritePage.defaultProps = {
  data: {},
};

EventBritePage.propTypes = {
  data: PropTypes.object,
};

export const pageQuery = graphql`
  query EventsQuery($slug: String!) {
    eventBriteEvent(fields: { slug: { eq: $slug } }) {
      locale
      id
      description
      created(formatString: "DD MMMM YYYY", locale: "sv")
      start {
        local(formatString: "HH:mm", locale: "sv")
        utc(formatString: "DD MMMM YYYY", locale: "sv")
      }
      end {
        local(formatString: "HH:mm", locale: "sv")
        utc(formatString: "DD MMMM YYYY", locale: "sv")
      }
      logo {
        id
        original {
          url
          width
          height
        }
        url
      }
      url
      venue {
        address {
          address_1
          address_2
          city
          postal_code
          country
          latitude
          longitude
        }
      }
      venue_id
      url
      name {
        text
        html
      }
      fields {
        slug
      }
    }
  }
`;
